import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Room, Voter, CreateRoomRequest, UpdateRoomRequest, EstimateType, DualEstimateConfig } from '../types/api'
import { apiClient } from '../services/apiClient'
import { pollingService } from '../services/pollingService'

export const useRoomStore = defineStore('room', () => {
  // State
  const room = ref<Room | null>(null)
  const voters = ref<Voter[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pollingActive = ref(false)

  // Computed getters for crowd indicator
  const estimationViewerCount = computed(() =>
    voters.value.filter(v => v.activeTab === 'estimation').length
  )

  const confidenceViewerCount = computed(() =>
    voters.value.filter(v => v.activeTab === 'confidence').length
  )

  const retrospectiveViewerCount = computed(() =>
    voters.value.filter(v => v.activeTab === 'retrospective').length
  )

  const totalActiveViewers = computed(() =>
    voters.value.filter(v => v.activeTab !== null).length
  )

  // Computed getter for dual estimate mode awareness
  const isDualEstimateEnabled = computed(() => 
    room.value?.estimationMode === 'dual'
  )

  // Computed getter for dual estimate labels with defaults
  const dualEstimateLabelConfig = computed((): DualEstimateConfig => {
    if (room.value?.dualEstimateLabels) {
      return room.value.dualEstimateLabels
    }
    return {
      primaryLabel: 'With AI',
      secondaryLabel: 'Without AI'
    }
  })

  // Actions
  async function fetchRoom(roomKey: string): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      const roomWithVoters = await apiClient.getRoom(roomKey)
      room.value = roomWithVoters.room
      voters.value = roomWithVoters.voters
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch room'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchRoomName(roomKey: string): Promise<string> {
    try {
      error.value = null
      
      const roomName = await apiClient.getRoomName(roomKey)
      return roomName
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch room name'
      throw err
    }
  }

  async function fetchRoomSilent(roomKey: string): Promise<void> {
    try {
      // Don't set loading state for background polling
      error.value = null
      
      const roomWithVoters = await apiClient.getRoom(roomKey)
      room.value = roomWithVoters.room
      voters.value = roomWithVoters.voters
    } catch (err: any) {
      // Don't set error state for polling failures to avoid disrupting UI
      // Polling errors are handled by connection warning logic
      throw err
    }
  }

  async function createRoom(roomName: string, estimationMode: 'single' | 'dual' = 'single'): Promise<Room> {
    try {
      loading.value = true
      error.value = null
      
      const request: CreateRoomRequest = { 
        roomName,
        estimationMode 
      }
      
      // Add default labels when dual mode is selected
      if (estimationMode === 'dual') {
        request.dualEstimateLabels = {
          primaryLabel: 'With AI',
          secondaryLabel: 'Without AI'
        }
      }
      
      const createdRoom = await apiClient.createRoom(request)
      
      room.value = createdRoom
      voters.value = [] // Start with empty voters array
      
      return createdRoom
    } catch (err: any) {
      error.value = err.message || 'Failed to create room'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateSubject(roomKey: string, subject: string): Promise<void> {
    try {
      error.value = null
      
      const request: UpdateRoomRequest = { subject }
      const updatedRoom = await apiClient.updateRoom(roomKey, request)
      
      if (room.value) {
        room.value = updatedRoom
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to update subject'
      throw err
    }
  }

  async function revealVotes(roomKey: string, voteType: 'estimation' | 'confidence'): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      if (voteType === 'estimation') {
        await apiClient.revealEstimationVotes(roomKey)
      } else {
        await apiClient.revealConfidenceVotes(roomKey)
      }
      
      // Refresh room state after revealing votes (silent to avoid loading flicker)
      await fetchRoomSilent(roomKey)
    } catch (err: any) {
      error.value = err.message || 'Failed to reveal votes'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function clearVotes(roomKey: string, voteType: 'estimation' | 'confidence'): Promise<void> {
    try {
      loading.value = true
      error.value = null
      
      if (voteType === 'estimation') {
        await apiClient.clearEstimationVotes(roomKey)
      } else {
        await apiClient.clearConfidenceVotes(roomKey)
      }
      
      // Refresh room state after clearing votes (silent to avoid loading flicker)
      await fetchRoomSilent(roomKey)
    } catch (err: any) {
      error.value = err.message || 'Failed to clear votes'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function startEstimationPokerTimer(roomKey: string): Promise<void> {
    try {
      error.value = null
      
      const updatedRoom = await apiClient.startEstimationPokerTimer(roomKey)
      
      if (room.value) {
        room.value = updatedRoom
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to start timer'
      throw err
    }
  }

  function startPolling(roomKey: string, intervalMs?: number): void {
    if (pollingActive.value) {
      stopPolling()
    }
    
    pollingActive.value = true
    
    pollingService.start(async () => {
      try {
        await fetchRoomSilent(roomKey)
      } catch (err) {
        // Polling errors are handled silently to avoid disrupting the UI
        // Connection warnings are managed by the UI store based on consecutive failures
        console.warn('Polling failed:', err)
      }
    }, intervalMs)
  }

  function stopPolling(): void {
    pollingService.stop()
    pollingActive.value = false
  }

  function clearError(): void {
    error.value = null
  }

  /**
   * Optimistically update a voter's vote in the local voters array.
   * This provides immediate UI feedback before the next poll refresh.
   */
  function updateVoterVote(
    voterKey: string,
    voteType: 'estimation' | 'confidence',
    value: number | string,
    votingRound: number
  ): void {
    const voterIndex = voters.value.findIndex(v => v.voterKey === voterKey)
    if (voterIndex === -1) return

    const voter = voters.value[voterIndex]
    if (voteType === 'estimation') {
      voter.estimationPokerVote = { value, votingRound }
    } else {
      voter.confidenceVote = { value: value as number, votingRound }
    }
  }

  /**
   * Optimistically update a voter's dual estimate vote in the local voters array.
   * This provides immediate UI feedback before the next poll refresh.
   */
  function updateVoterDualVote(
    voterKey: string,
    estimateType: EstimateType,
    value: number | string,
    votingRound: number
  ): void {
    const voterIndex = voters.value.findIndex(v => v.voterKey === voterKey)
    if (voterIndex === -1) return

    const voter = voters.value[voterIndex]
    if (!voter.estimationPokerDualVotes) {
      voter.estimationPokerDualVotes = {} as Record<EstimateType, { value: number | string; votingRound: number }>
    }
    voter.estimationPokerDualVotes[estimateType] = { value, votingRound }
  }

  /**
   * Optimistically update a voter's activeTab in the local voters array.
   * This provides immediate UI feedback for crowd indicator counts.
   */
  function updateVoterActiveTab(
    voterKey: string,
    activeTab: 'estimation' | 'confidence' | 'retrospective'
  ): void {
    const voterIndex = voters.value.findIndex(v => v.voterKey === voterKey)
    if (voterIndex === -1) return

    voters.value[voterIndex].activeTab = activeTab
  }

  function resetState(): void {
    room.value = null
    voters.value = []
    loading.value = false
    error.value = null
    stopPolling()
  }

  return {
    // State
    room,
    voters,
    loading,
    error,
    pollingActive,
    // Computed getters for crowd indicator
    estimationViewerCount,
    confidenceViewerCount,
    retrospectiveViewerCount,
    totalActiveViewers,
    // Computed getters for dual estimate mode
    isDualEstimateEnabled,
    dualEstimateLabelConfig,
    // Actions
    fetchRoom,
    fetchRoomName,
    fetchRoomSilent,
    createRoom,
    updateSubject,
    revealVotes,
    clearVotes,
    startEstimationPokerTimer,
    startPolling,
    stopPolling,
    clearError,
    resetState,
    updateVoterVote,
    updateVoterDualVote,
    updateVoterActiveTab,
  }
})